import type { Block, Inline } from '@/lib/markdown';
import { parseMarkdown } from '@/lib/markdown';
import { cn } from '@/lib/utils';

/**
 * What the agent wrote, as blocks rather than as a wall of asterisks.
 *
 * Deliberately typographically quiet: this sits beside a decision, not in an
 * article. Headings are the eyebrow the rest of the console uses, lists are
 * tight, and code and tables keep their own background or border and scroll
 * on their own axis rather than pushing the region that holds them wide.
 *
 * Every node here is built from the parser's data. Nothing is ever handed to
 * `dangerouslySetInnerHTML`, so a report containing markup renders as the text
 * of that markup, and the only attribute a report can influence is a link's
 * `href` - which `safeHref` has already restricted to http, https and mailto.
 */
function Spans({ spans }: { spans: Inline[] }) {
  return (
    <>
      {spans.map((span, index) => {
        // Spans have no identity of their own, and the list is rebuilt whole
        // whenever the text changes, so the index is the key.
        const key = `${span.type}-${index}`;
        if (span.type === 'code') {
          return (
            <code key={key} className="bg-raised rounded-sm px-1 py-px font-mono text-[0.92em]">
              {span.text}
            </code>
          );
        }
        if (span.type === 'strong') {
          return (
            <strong key={key} className="text-foreground font-medium">
              {span.text}
            </strong>
          );
        }
        if (span.type === 'link') {
          return (
            <a
              key={key}
              href={span.href}
              target="_blank"
              rel="noreferrer noopener"
              className="text-info underline decoration-dotted underline-offset-2"
            >
              {span.text}
            </a>
          );
        }
        return <span key={key}>{span.text}</span>;
      })}
    </>
  );
}

function BlockView({ block }: { block: Block }) {
  if (block.type === 'heading') {
    // Visible hierarchy, not one size wearing every level (issue #64): a
    // level 1 or 2 heading is the plan's own section break and reads at
    // `text-sm` with real weight; a level 3 and deeper is the quieter eyebrow
    // the rest of the console already uses for a group label.
    return block.level <= 2 ? (
      <div className="text-foreground mt-3 text-sm font-semibold first:mt-0">
        <Spans spans={block.spans} />
      </div>
    ) : (
      <div className="dy-eyebrow mt-2.5 first:mt-0">
        <Spans spans={block.spans} />
      </div>
    );
  }
  if (block.type === 'code') {
    return (
      <pre className="bg-sunken dy-scroll mt-1.5 overflow-x-auto rounded-sm border p-2 font-mono text-2xs">
        {block.text}
      </pre>
    );
  }
  if (block.type === 'list') {
    const List = block.ordered ? 'ol' : 'ul';
    return (
      <List
        className={cn(
          'mt-1.5 ml-4 space-y-0.5',
          block.ordered ? 'list-decimal' : 'list-disc',
          'marker:text-fg-3',
        )}
      >
        {block.items.map((item, index) => (
          // A parsed report is positional and immutable: the whole list is
          // rebuilt from the string whenever it changes, and nothing is ever
          // reordered or removed in place, so position IS the identity here.
          // biome-ignore lint/suspicious/noArrayIndexKey: see above.
          <li key={`item-${index}`}>
            <Spans spans={item} />
          </li>
        ))}
      </List>
    );
  }
  if (block.type === 'table') {
    // One class string per column, built once rather than at each cell: the
    // suppression comment on a table cell only applies to the line right after
    // it, and a `cn(...)` call inline at the cell site is long enough that the
    // formatter wraps the key onto its own line and breaks that adjacency.
    const headCell = (index: number) =>
      cn(
        'text-fg-2 px-2 py-1 font-medium whitespace-nowrap',
        block.align[index] === 'right' && 'text-right',
        block.align[index] === 'center' && 'text-center',
      );
    const bodyCell = (index: number) =>
      cn(
        'px-2 py-1 align-top',
        block.align[index] === 'right' && 'text-right',
        block.align[index] === 'center' && 'text-center',
      );
    // Scrolls on its own axis rather than pushing the pane wide, same as the
    // fenced code block above: a plan's table is as likely to carry a long
    // path in a cell as a report's fence is to carry a long line.
    return (
      <div className="dy-scroll mt-1.5 overflow-x-auto">
        <table className="w-full border-collapse text-left text-xs">
          <thead>
            <tr className="border-b">
              {block.header.map((cell, index) => (
                // Positional, for the same reason as the list above: a table is
                // rebuilt whole from the string, never reordered in place.
                // biome-ignore lint/suspicious/noArrayIndexKey: see above.
                <th key={`h-${index}`} className={headCell(index)}>
                  <Spans spans={cell} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, rowIndex) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: see above.
              <tr key={`r-${rowIndex}`} className="border-b last:border-b-0 even:bg-raised/40">
                {row.map((cell, cellIndex) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: see above.
                  <td key={`c-${cellIndex}`} className={bodyCell(cellIndex)}>
                    <Spans spans={cell} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return (
    <p className="mt-1.5 whitespace-pre-wrap first:mt-0">
      <Spans spans={block.spans} />
    </p>
  );
}

export function Markdown({ source, className }: { source: string; className?: string }) {
  const blocks = parseMarkdown(source);
  if (!blocks.length) return null;
  return (
    <div className={cn('text-fg-2 break-words', className)}>
      {blocks.map((block, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: as in the list above.
        <BlockView key={`${block.type}-${index}`} block={block} />
      ))}
    </div>
  );
}
